import { useMemo, useState } from 'react';
import {
  Button,
  Card,
  Empty,
  Grid,
  Input,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  ArrowDown,
  ArrowUp,
  BarChart3,
  Pencil,
  Plus,
  ThumbsDown,
  ThumbsUp,
  Trash2,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import { useDebounce } from '@shared/hooks/useDebounce';
import { PageHeader } from '@shared/components/PageHeader/PageHeader';
import { StatusBadge } from '@shared/components/StatusBadge';
import { SortableList } from '@shared/components/SortableList/SortableList';
import { LanguagePicker } from '@shared/components/LanguagePicker/LanguagePicker';
import { sssApi } from '@api/sss';
import { categoriesApi } from '@api/categories';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { recordAuditAction } from '@features/audit/recordAction';
import { useCan } from '@shared/rbac/useCan';
import { PERMISSIONS } from '@shared/rbac/permissions.const';
import { ROUTES } from '@shared/constants/routes';
import {
  SSS_FILTER_STATUSES,
  toContentStatus,
  useSssStatusLabel,
  type ReorderEntry,
  type SssFaq,
  type SssSortBy,
  type SssStatus,
} from '../types';

const { Text } = Typography;
const { useBreakpoint } = Grid;

type StatusFilter = SssStatus | 'ALL';

export function SssListPage() {
  const { t } = useTranslation(['sss', 'common', 'validation', 'errors']);
  const statusLabel = useSssStatusLabel();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const canCreate = useCan(PERMISSIONS.SSS_CREATE);
  const canUpdate = useCan(PERMISSIONS.SSS_UPDATE);
  const canDelete = useCan(PERMISSIONS.SSS_DELETE);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [categoryId, setCategoryId] = useState<string | undefined>(undefined);
  const [languageId, setLanguageId] = useState<string | undefined>(undefined);
  const [sortBy, setSortBy] = useState<SssSortBy>('order');
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  // Local override applied after optimistic drag-drop reorder (until next refetch settles).
  const [override, setOverride] = useState<SssFaq[] | null>(null);

  const hasActiveFilters =
    !!debouncedSearch ||
    statusFilter !== 'ALL' ||
    !!categoryId ||
    !!languageId ||
    sortBy !== 'order';

  const resetFilters = () => {
    setSearch('');
    setStatusFilter('ALL');
    setCategoryId(undefined);
    setLanguageId(undefined);
    setSortBy('order');
  };

  const { data, isFetching } = useQuery({
    queryKey: [
      'sss',
      { search: debouncedSearch, statusFilter, categoryId, languageId, sortBy },
    ],
    queryFn: () =>
      sssApi.list({
        search: debouncedSearch,
        // K-13 #19 — 6 durumun tamamı BE `?status=` filtresiyle süzülür.
        status: statusFilter,
        categoryId,
        languageId,
        sortBy,
        pageSize: 200,
      }),
    placeholderData: (prev) => prev,
  });

  // Derived list — drops local override when a fresh server payload arrives.
  // K-13 #19: durum süzgeci artık BE'de (client-side DRAFT filtresi kalktı).
  const items = useMemo<SssFaq[]>(
    () => override ?? data?.items ?? [],
    [override, data],
  );

  const { data: categoriesData } = useQuery({
    queryKey: ['categories', { scope: 'SSS' }],
    queryFn: () => categoriesApi.list({ scope: 'SSS' }),
    retry: false,
  });
  const categoryMap = useMemo(() => {
    const m = new Map<string, string>();
    (categoriesData?.items ?? []).forEach((c) => m.set(c.id, c.name));
    return m;
  }, [categoriesData]);

  const reorderMutation = useMutation({
    mutationFn: (entries: ReorderEntry[]) => sssApi.reorder(entries),
    onSuccess: async () => {
      toast.success(t('toast.reordered'));
      await qc.invalidateQueries({ queryKey: ['sss'] });
      setOverride(null); // server is source of truth again
      void recordAuditAction({
        action: 'sss.update',
        resource: 'sss',
        summary: 'SSS sıralaması güncellendi',
      });
    },
    onError: (err) => {
      // Roll back optimistic override on failure.
      setOverride(null);
      toast.error(resolveApiErrorMessage(err));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (faq: SssFaq) => sssApi.remove(faq.id),
    onSuccess: async (_d, faq) => {
      toast.success(t('toast.deleted', { question: faq.question }));
      await qc.invalidateQueries({ queryKey: ['sss'] });
      void recordAuditAction({
        action: 'sss.delete',
        resource: 'sss',
        resourceId: faq.id,
        summary: `${faq.question} sorusu silindi`,
      });
    },
    onError: (err) => {
      toast.error(resolveApiErrorMessage(err));
    },
    onSettled: () => setPendingDeleteId(null),
  });

  const commitReorder = (next: SssFaq[]) => {
    const withOrder = next.map((it, idx) => ({ ...it, order: idx + 1 }));
    setOverride(withOrder);
    const entries: ReorderEntry[] = withOrder.map((it) => ({
      id: it.id,
      order: it.order,
    }));
    reorderMutation.mutate(entries);
  };

  const moveItem = (index: number, delta: -1 | 1) => {
    const target = index + delta;
    if (target < 0 || target >= items.length) return;
    const next = items.slice();
    const moved = next.splice(index, 1)[0];
    if (!moved) return;
    next.splice(target, 0, moved);
    commitReorder(next);
  };

  const canReorder =
    canUpdate &&
    sortBy === 'order' &&
    statusFilter === 'ALL' &&
    !debouncedSearch &&
    !categoryId &&
    !languageId;

  const renderRow = (item: SssFaq, idx: number) => {
    const isDeleting = pendingDeleteId === item.id && deleteMutation.isPending;
    const goEdit = () => void navigate(ROUTES.SSS_EDIT.replace(':id', item.id));
    return (
      <div className="sss-row">
        <div className="sss-row__main">
          <div className="sss-row__question">
            <Button
              type="link"
              onClick={goEdit}
              style={{
                padding: 0,
                height: 'auto',
                whiteSpace: 'normal',
                textAlign: 'left',
                fontWeight: 600,
                color: 'var(--ds-text-primary)',
              }}
              aria-label={t('list.editAria', { question: item.question })}
            >
              {item.question}
            </Button>
            <Space size={6} wrap style={{ marginTop: 4 }}>
              <Tooltip title={statusLabel(item.status)}>
                <span>
                  <StatusBadge kind="content" status={toContentStatus(item.status)} size="sm" />
                </span>
              </Tooltip>
              {item.categoryId ? (
                <Tag style={{ margin: 0 }}>
                  {categoryMap.get(item.categoryId) ?? t('list.categoryFallback')}
                </Tag>
              ) : null}
              <Tag style={{ margin: 0, fontVariantNumeric: 'tabular-nums' }}>
                #{item.order}
              </Tag>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <ThumbsUp size={11} style={{ verticalAlign: -2 }} />{' '}
                {item.helpfulYesCount.toLocaleString('tr-TR')}
                {'  '}
                <ThumbsDown size={11} style={{ verticalAlign: -2 }} />{' '}
                {item.helpfulNoCount.toLocaleString('tr-TR')}
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {dayjs(item.updatedAt).format('DD.MM.YYYY')}
              </Text>
            </Space>
          </div>
        </div>
        <Space className="sss-row__actions">
          {isMobile && canReorder ? (
            <>
              <Tooltip title={t('list.moveUp')}>
                <Button
                  size="small"
                  icon={<ArrowUp size={14} />}
                  aria-label={t('list.moveUp')}
                  disabled={idx === 0 || reorderMutation.isPending}
                  onClick={() => moveItem(idx, -1)}
                />
              </Tooltip>
              <Tooltip title={t('list.moveDown')}>
                <Button
                  size="small"
                  icon={<ArrowDown size={14} />}
                  aria-label={t('list.moveDown')}
                  disabled={idx === items.length - 1 || reorderMutation.isPending}
                  onClick={() => moveItem(idx, 1)}
                />
              </Tooltip>
            </>
          ) : null}
          <Tooltip title={t('common:actions.edit')}>
            <Button
              size="small"
              icon={<Pencil size={14} />}
              aria-label={t('common:actions.edit')}
              onClick={goEdit}
            />
          </Tooltip>
          {canDelete ? (
            <Popconfirm
              title={t('list.deleteTitle')}
              description={t('list.deleteDescription', { question: item.question })}
              okText={t('common:actions.delete')}
              okButtonProps={{ danger: true, loading: isDeleting }}
              cancelText={t('common:actions.cancel')}
              onConfirm={() => {
                setPendingDeleteId(item.id);
                deleteMutation.mutate(item);
              }}
            >
              <Tooltip title={t('common:actions.delete')}>
                <Button
                  size="small"
                  danger
                  icon={<Trash2 size={14} />}
                  aria-label={t('common:actions.delete')}
                  loading={isDeleting}
                  disabled={deleteMutation.isPending && !isDeleting}
                />
              </Tooltip>
            </Popconfirm>
          ) : null}
        </Space>
      </div>
    );
  };

  return (
    <div>
      <PageHeader
        title={t('list.title')}
        description={t('list.description')}
        breadcrumb={[{ label: t('common:labels.home'), to: '/' }, { label: 'SSS' }]}
        actions={
          <Space wrap>
            <Button
              icon={<BarChart3 size={14} />}
              onClick={() => void navigate(ROUTES.SSS_ANALYTICS)}
            >
              {t('list.analytics')}
            </Button>
            {canCreate ? (
              <Button
                type="primary"
                icon={<Plus size={14} />}
                onClick={() => void navigate(ROUTES.SSS_NEW)}
              >
                {t('list.new')}
              </Button>
            ) : null}
          </Space>
        }
      />

      <Card>
        <div className="sss-toolbar">
          <Input.Search
            placeholder={t('list.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="sss-toolbar__search"
            allowClear
          />
          {/* K-13 #19 — 6 durumun tamamı BE ?status= filtresiyle (granüler etiketler). */}
          <Select<StatusFilter>
            value={statusFilter}
            onChange={(v) => setStatusFilter(v)}
            className="sss-toolbar__status"
            popupMatchSelectWidth={false}
            options={[
              { value: 'ALL', label: t('common:labels.all') },
              ...SSS_FILTER_STATUSES.map((st) => ({
                value: st,
                label: statusLabel(st),
              })),
            ]}
          />
          <Select<string>
            allowClear
            placeholder={t('list.categoryPlaceholder')}
            value={categoryId}
            onChange={setCategoryId}
            className="sss-toolbar__category"
            options={(categoriesData?.items ?? []).map((c) => ({
              value: c.id,
              label: c.name,
            }))}
          />
          <LanguagePicker
            value={languageId}
            onChange={setLanguageId}
            allowClear
            placeholder={t('common:contentLanguage.filterAll')}
            className="sss-toolbar__category"
          />
          {isMobile ? (
            <Select<SssSortBy>
              value={sortBy}
              onChange={(v) => setSortBy(v)}
              className="sss-toolbar__sort"
              options={[
                { value: 'order', label: t('sort.order') },
                { value: 'recent', label: t('sort.recent') },
                { value: 'helpful', label: t('sort.helpful') },
              ]}
            />
          ) : (
            <Segmented<SssSortBy>
              value={sortBy}
              onChange={(v) => setSortBy(v)}
              options={[
                { value: 'order', label: t('sort.order') },
                { value: 'recent', label: t('sort.recent') },
                { value: 'helpful', label: t('sort.helpful') },
              ]}
            />
          )}
          {hasActiveFilters ? (
            <Button onClick={resetFilters} size={isMobile ? 'middle' : 'small'}>
              {t('list.clearFilters')}
            </Button>
          ) : null}
        </div>

        {canReorder && items.length > 0 ? (
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
            {isMobile
              ? t('list.reorderHintMobile')
              : t('list.reorderHintDesktop')}
          </Text>
        ) : null}

        {isFetching && items.length === 0 ? (
          <div style={{ padding: 64, textAlign: 'center' }}>
            <Spin />
          </div>
        ) : items.length === 0 ? (
          <Empty description={hasActiveFilters ? t('list.emptyFiltered') : t('list.empty')}>
            {hasActiveFilters ? (
              <Button onClick={resetFilters}>{t('list.clearFilters')}</Button>
            ) : null}
          </Empty>
        ) : canReorder ? (
          <SortableList<SssFaq>
            items={items}
            getId={(it) => it.id}
            onReorder={commitReorder}
            disabled={isMobile || reorderMutation.isPending}
            renderItem={renderRow}
          />
        ) : (
          <div className="ds-sortable">
            {items.map((it, idx) => (
              <div key={it.id} className="ds-sortable__item" data-dragging="false">
                <div />
                <div style={{ minWidth: 0 }}>{renderRow(it, idx)}</div>
                <div />
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
